import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReferralURLConfig } from '@/utils/config';
import { Loader2 } from 'lucide-react';
import useCampaigns from '@/hooks/useCampaigns';
import { CampaignIcon } from '@/components/icons/CustomIcons';

const templateSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  template: z.string()
    .min(10, { message: "Template must be at least 10 characters" })
    .refine(val => val.includes('{referralLink}'), {
      message: "Template must include {referralLink} placeholder"
    }),
  linkSource: z.enum(['url', 'campaign']), // New field to switch between URL and campaign
  urlConfigId: z.string().optional(),
  campaignId: z.string().optional(),
  isDefault: z.boolean().default(false),
}).refine(data => {
  if (data.linkSource === 'url') {
    return !!data.urlConfigId;
  } else {
    return !!data.campaignId;
  }
}, {
  message: "Please select a URL or Campaign",
  path: ["urlConfigId"]
});

export type TemplateFormData = z.infer<typeof templateSchema>;

interface TemplateFormProps {
  urls: ReferralURLConfig[];
  initialData?: TemplateFormData;
  onSubmit: (data: TemplateFormData) => Promise<void>;
  isSubmitting: boolean;
  onCancel: () => void;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  urls,
  initialData,
  onSubmit,
  isSubmitting,
  onCancel,
}) => {
  const [linkSource, setLinkSource] = useState<'url' | 'campaign'>(
    initialData?.campaignId ? 'campaign' : 'url'
  );
  const { campaigns } = useCampaigns();
  const activeCampaigns = campaigns.filter(c => c.isActive);

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: initialData || {
      name: '',
      template: 'Hey! Check out Smart Screenz using my referral link: {referralLink} for Smart Screenz Thanks.',
      linkSource: 'url',
      urlConfigId: urls.length > 0 ? (urls.find(u => u.isDefault)?.id || urls[0].id) : '',
      campaignId: '',
      isDefault: false,
    },
  });

  // Update form values when linkSource changes
  useEffect(() => {
    form.setValue('linkSource', linkSource);
    
    // Clear the other field when switching
    if (linkSource === 'url') {
      form.setValue('campaignId', '');
    } else {
      form.setValue('urlConfigId', '');
    }
  }, [linkSource, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Template Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Default Template" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <FormLabel>Link Source</FormLabel>
          <Tabs
            value={linkSource}
            onValueChange={(value) => setLinkSource(value as 'url' | 'campaign')}
            className="mb-4"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="url">URL</TabsTrigger>
              <TabsTrigger value="campaign" className="flex items-center">
                <CampaignIcon />
                Campaign
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {linkSource === 'url' ? (
            <FormField
              control={form.control}
              name="urlConfigId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Associated URL</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a URL" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {urls.map((url) => (
                        <SelectItem key={url.id} value={url.id}>
                          {url.name} {url.isDefault && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <FormField
              control={form.control}
              name="campaignId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Associated Campaign</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a campaign" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activeCampaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.title} {campaign.status === 'active' && '(Active)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Links templates to active campaigns for automatic referral link generation
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <FormField
          control={form.control}
          name="template"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message Template</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter your referral message template"
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormDescription>
                Use {'{referralLink}'} as a placeholder for the referral link
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Set as Default</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Make this the default template for referrals
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Template'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default TemplateForm;
